import { Inventory2Outlined, TouchAppOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { LotStorageLocationDto, LotStorageSourceValue } from '@prospero/shared';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useLotStorageSuggestions } from '../../../hooks/lot-storage/use-lot-storage-suggestions';
import { useStoragePlacementOptions } from '../../../hooks/lot-storage/use-storage-placement-options';
import { WizardNavigation } from '../components/wizard-navigation';
import { WizardStepHeader } from '../components/wizard-step-header';
import { usePatchDraft } from '../hooks/use-draft-mutations';
import { getStepRoute, useWizardDraft } from '../hooks/use-wizard-draft';
import { WizardLayout } from './wizard-layout';

interface ItemSelection {
  location: LotStorageLocationDto;
  source: LotStorageSourceValue;
}

export function StorageStepPage() {
  const { t } = useTranslation('goodsReceiving');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');

  const { data: draft, isLoading: draftLoading } = useWizardDraft(draftId);
  const { data: suggestions } = useLotStorageSuggestions(draftId);
  const patchDraft = usePatchDraft();

  const [selections, setSelections] = useState<Record<string, ItemSelection>>({});

  const { placementOptions } = useStoragePlacementOptions();

  const suggestionByItemId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof suggestions>[number]>();
    for (const suggestion of suggestions ?? []) {
      map.set(suggestion.itemId, suggestion);
    }
    return map;
  }, [suggestions]);

  const applyAllSuggestions = useCallback(() => {
    setSelections((prev) => {
      const next: Record<string, ItemSelection> = { ...prev };
      for (const item of draft?.items ?? []) {
        if (next[item.id]) {
          continue;
        }
        const suggestion = suggestionByItemId.get(item.id);
        if (suggestion?.location) {
          next[item.id] = { location: suggestion.location, source: suggestion.source ?? 'MANUAL' };
        }
      }
      return next;
    });
  }, [draft, suggestionByItemId]);

  const acceptSuggestion = useCallback(
    (itemId: string, location: LotStorageLocationDto, source: LotStorageSourceValue) => {
      setSelections((prev) => ({ ...prev, [itemId]: { location, source } }));
    },
    [],
  );

  const clearSelection = useCallback((itemId: string) => {
    setSelections((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  const persistAndNavigate = useCallback(
    (step: 'storage' | 'summary') => {
      if (!draftId) {
        return;
      }
      const targetStep = step === 'summary' ? 'summary' : 'summary';
      const items = (draft?.items ?? [])
        .filter((item) => selections[item.id])
        .map((item) => {
          const selection = selections[item.id];
          return {
            id: item.id,
            storageEquipmentId:
              selection.location.type === 'EQUIPMENT' ? selection.location.id : undefined,
            storageCleaningAreaId:
              selection.location.type === 'CLEANING_AREA' ? selection.location.id : undefined,
            storageAssignmentSource: selection.source,
          };
        });

      patchDraft.mutate(
        {
          id: draftId,
          data: { currentStep: targetStep, items: items.length > 0 ? items : undefined },
        },
        {
          onSuccess: () =>
            navigate(`${getStepRoute('summary')}?draftId=${draftId}`, { flushSync: true }),
        },
      );
    },
    [draftId, draft, selections, patchDraft, navigate],
  );

  if (draftLoading) {
    return (
      <WizardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </WizardLayout>
    );
  }

  const items = draft?.items ?? [];
  const hasSuggestions = (suggestions ?? []).some((s) => s.location);

  return (
    <WizardLayout>
      <WizardStepHeader
        icon={<Inventory2Outlined />}
        title={t('lotStorage.stepTitle')}
        subtitle={t('lotStorage.stepDescription')}
        step="storage"
      />

      {hasSuggestions && (
        <Button variant="outlined" onClick={applyAllSuggestions} sx={{ mt: 2, mb: 1 }} fullWidth>
          {t('lotStorage.applyAllSuggestions')}
        </Button>
      )}

      <Stack spacing={2} sx={{ mt: 2, mb: 10 }}>
        {items.map((item) => {
          const suggestion = suggestionByItemId.get(item.id);
          const selection = selections[item.id];
          const selectedOption =
            placementOptions.find(
              (option) =>
                option.type === selection?.location.type && option.id === selection?.location.id,
            ) ?? null;

          return (
            <Box key={item.id} data-testid={`storage-item-${item.productName}`}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 0.75,
                }}
              >
                <Typography sx={{ flex: 1, minWidth: 120 }}>{item.productName}</Typography>
                {!selection && suggestion?.location && (
                  <Chip
                    icon={<TouchAppOutlined />}
                    label={t('lotStorage.suggestionChipLabel', {
                      location: suggestion.location.name,
                    })}
                    aria-label={t('lotStorage.suggestionChipAriaLabel', {
                      location: suggestion.location.name,
                    })}
                    color={suggestion.confidence === 'high' ? 'success' : 'info'}
                    variant="outlined"
                    onClick={() => {
                      if (suggestion.location) {
                        acceptSuggestion(
                          item.id,
                          suggestion.location,
                          suggestion.source ?? 'MANUAL',
                        );
                      }
                    }}
                    sx={{
                      fontWeight: 600,
                      borderWidth: 1.5,
                      '& .MuiChip-icon': { fontSize: 18 },
                    }}
                  />
                )}
              </Box>
              <Autocomplete
                options={placementOptions}
                groupBy={(option) => option.groupLabel}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.type === value.type && option.id === value.id
                }
                value={selectedOption}
                onChange={(_, option) => {
                  if (option) {
                    setSelections((prev) => ({
                      ...prev,
                      [item.id]: {
                        location: { type: option.type, id: option.id, name: option.name },
                        source: 'MANUAL',
                      },
                    }));
                  } else {
                    clearSelection(item.id);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t('lotStorage.choosePlacement')} />
                )}
                size="small"
                sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
              />
            </Box>
          );
        })}
      </Stack>

      <WizardNavigation
        canGoBack
        canGoNext
        onBack={() =>
          navigate(`${getStepRoute('checklist')}?draftId=${draftId}`, { flushSync: true })
        }
        onNext={() => persistAndNavigate('summary')}
        isLoading={patchDraft.isPending}
      />
    </WizardLayout>
  );
}
